import { memo, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router";
import { Box } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import MetaTags from "react-meta-tags";

import SkeletonDetail from "components/catalog/SkeletonDetail";
import { PanelSidebar } from "components/PanelSidebar";
import { PanelDetailContent as RightContent } from "components/PanelDetailContent";
import Page from "components/wrappers/Page";
import { DESIGNS } from "helpers/apis";
import useAxios from "helpers/axios";
import useAuth from "hooks/useAuth";
import useCatalogDetail from "hooks/useCatalogDetail";
import useDialog from "hooks/useDialog";
import { ICustomPanelForm } from "types/form";
import { IDesign } from "types/design";
import { validationSchema } from "./index";

const PanelDetail = ({ customize = false }: { customize?: boolean }) => {
  const { id: catalogId } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const {
    catalog,
    customize: customizeInContext,
    customPanelAction,
    setCatalog,
    setCustomize,
    setIsCustomPanel,
  } = useCatalogDetail();
  const { isLoggedIn } = useAuth();
  const { setOpenLoginDialog } = useDialog();

  const { fetchData } = useAxios({
    config: {
      method: "GET",
    },
    callOnInit: false,
  });

  useEffect(() => {
    setCustomize(customize);
    setIsCustomPanel(false);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [pathname]);

  useEffect(() => {
    fetchData(
      {
        url: `${DESIGNS}${catalogId}`,
      },
      ({
        response: { data },
      }: {
        response: {
          data: IDesign;
        };
      }) => {
        setCatalog(data);
      },
      isLoggedIn
        ? undefined
        : () => {
            setOpenLoginDialog(true);
          }
    );
  }, [catalogId, isLoggedIn, setOpenLoginDialog]);

  const isOnCustomize = useMemo(() => {
    return customizeInContext || customize;
  }, [customizeInContext, customize]);

  if (!catalog) {
    return <SkeletonDetail />;
  }

  const onSubmit = (
    values: ICustomPanelForm,
    actions: FormikHelpers<ICustomPanelForm>
  ) => {
    customPanelAction({
      values,
      actions,
    });
  };

  return (
    <Box width={1}>
      {!isOnCustomize && (
        <MetaTags>
          <title>{`${catalog?.name || ""} | Assay Marketplace`}</title>
          <link
            rel="canonical"
            href={`https://assay-marketplace.archerdx.com/catalog/detail/${catalogId}`}
          />
        </MetaTags>
      )}

      <Formik
        initialValues={{
          panelName: isOnCustomize ? "" : catalog?.name || "",
          panels: [],
        }}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <Page>
            <PanelSidebar />
            <RightContent />
          </Page>
        </Form>
      </Formik>
    </Box>
  );
};

export default memo(PanelDetail);
